import { Dispatch, Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import Fuse from 'fuse.js';

import { AdvicesEntity } from '../../../../store/types/prescription';
import { GlobalState } from '../../../../store/reducers';
import { getAdvices } from '../../../../store/selectors/prescriptionPad';
import {
    deleteAdvice,
    DeleteAdviceAction,
    deleteAdvicePermanently,
    DeleteAdvicePermanentlyAction,
    EditAdviceAction,
    upsertAdvice,
    UpsertAdviceAction,
} from '../../../../store/actions/prescriptionPad';
import { LOCAL_ID_PREFIX } from '../../../../constants';
import { DB_TABLES } from '../../../../store/dbTables';
import { getGLocal } from '../../../../store/idbHandler';
import { GAdvice } from '../../../../store/db';
import EditAdvicesModal from './EditAdvicesModal';
import TinyMCEditor from '../../../common/TinyMceEditor';
import { uniqueBy } from '../../../../store/utils';

export const filterParsedText = (text: string) => {
    return text?.replace(/(\r\n|\n|\r)/gm, ' ');
};

const ADVICES: { [k: string]: string } = {
    'A-123': 'Please take some rest.',
    'A-423': 'Drink plenty of water.',
};

const ADVICES_LIST = Object.keys(ADVICES).map((k) => ({
    id: k,
    text: ADVICES[k],
    parsedText: ADVICES[k],
}));

type Props = {
    dispatch: Dispatch<
        UpsertAdviceAction | EditAdviceAction | DeleteAdviceAction | DeleteAdvicePermanentlyAction
    >;
    advices: AdvicesEntity[];
    rxDocId: string;
};

type State = {
    searchInput: string;
    allAdvices: AdvicesEntity[];
    editAdvice: AdvicesEntity | undefined;
};

const fuseConfig = { keys: ['searchText'], threshold: 0.4 };

const sortSelectedAdvicesFirst = (advices: AdvicesEntity[], allAdvices: AdvicesEntity[]) => {
    const combinedAdvices = [...advices];

    uniqueBy('id', allAdvices).forEach((i) => {
        if (!advices.find((a) => a.id === i.id)) {
            combinedAdvices.push(i);
        }
    });

    return combinedAdvices;
};

class Advices extends PureComponent<Props, State> {
    fuse = new Fuse(ADVICES_LIST, fuseConfig);

    state: State = {
        searchInput: '',
        allAdvices: [...ADVICES_LIST],
        editAdvice: undefined,
    };

    componentDidMount = async (): Promise<void> => {
        const { rxDocId } = this.props;
        const gAdvices = getGLocal<GAdvice>(rxDocId, DB_TABLES['advices']);
        const { advices } = this.props;

        Promise.all([gAdvices]).then((values) => {
            const allAdvices = [...values[0], ...this.state.allAdvices];
            const combinedAdvices = sortSelectedAdvicesFirst(advices, allAdvices).map((adv) => ({
                ...adv,
                searchText: filterParsedText(adv.parsedText || adv.text),
            }));

            this.setState({ allAdvices: combinedAdvices });
            this.fuse = new Fuse(combinedAdvices, fuseConfig);
        });
    };

    componentDidUpdate = (prevProps: Props): void => {
        const { allAdvices } = this.state;
        const { advices } = this.props;

        if (advices?.length !== prevProps.advices?.length) {
            const combinedAdvices = sortSelectedAdvicesFirst(advices, allAdvices);

            this.setState({
                allAdvices: combinedAdvices.map((adv) => ({
                    ...adv,
                    searchText: filterParsedText(adv.parsedText || adv.text),
                })),
            });

            this.fuse = new Fuse(
                combinedAdvices.map((adv) => ({
                    ...adv,
                    searchText: filterParsedText(adv.parsedText || adv.text),
                })),
                fuseConfig,
            );
        }
    };

    onInputChange = (text: string, parsedText: string): void => {
        this.setState({
            searchInput: filterParsedText(parsedText || text),
        });
    };

    onDeleteAdvice = (text: string, id: string): void => {
        const { dispatch } = this.props;

        dispatch(deleteAdvicePermanently(text, id));

        const advices = this.state.allAdvices.filter((advices) => advices.id !== id);
        this.fuse = new Fuse(advices, fuseConfig);
        this.setState({ allAdvices: advices });
    };
    onAdd = (a: AdvicesEntity) => {
        this.props.dispatch(upsertAdvice(a));
        this.onClearAdviceSearchInput();
        if (
            this.state.allAdvices.find(
                (ad) => ad.text.toLowerCase().trim() === a.text.toLowerCase().trim(),
            )
        ) {
            return;
        }
        const allAdvices = [
            { ...a, searchText: filterParsedText(a.parsedText || a.text) },
            ...this.state.allAdvices,
        ];
        this.setState({ allAdvices });
        this.fuse = new Fuse(allAdvices, fuseConfig);
    };

    onAddNewAdvice = (text: string, parsedText: string) => {
        const a = {
            id: `${LOCAL_ID_PREFIX}${new Date().getTime()}`,
            text: text,
            parsedText: parsedText,
            __isNew__: true,
        };
        this.onAdd(a);
    };

    onClearAdviceSearchInput = () => {
        this.setState({
            searchInput: '',
        });
    };

    onCheckboxChange = (a: AdvicesEntity & { searchText?: string }, checked: boolean) => () => {
        if (checked) {
            const advice = { ...a };
            delete advice?.searchText;
            this.props.dispatch(upsertAdvice(advice));
        } else {
            this.props.dispatch(deleteAdvice(a.id));
        }
        this.onClearAdviceSearchInput();
    };

    onEditAdvices = (a: AdvicesEntity) => {
        this.setState({ editAdvice: a });
    };

    onEditAdvicesCancel = () => {
        this.setState({ editAdvice: undefined });
    };

    setAllAdvices = (advices: AdvicesEntity[]) => {
        this.setState({ allAdvices: advices });
        this.fuse = new Fuse(advices, fuseConfig);
    };
    render() {
        const { searchInput, allAdvices, editAdvice } = this.state;
        const { advices } = this.props;

        const visibleList = searchInput
            ? this.fuse.search(searchInput).map((option) => option.item)
            : sortSelectedAdvicesFirst(advices, allAdvices);

        return (
            <Fragment>
                <div>
                    <TinyMCEditor
                        id="advices-editor"
                        inputValue={searchInput}
                        onInputChange={this.onInputChange}
                        onSave={this.onAddNewAdvice}
                        customOptions={{
                            inline: false,
                            height: 80,
                        }}
                        isFullScreen={true}
                        fullScreenHeightCssClass={246}
                        parentCssClasses="relative flex flex-col tiny-mce overflow-auto rounded-4 hide-scrollbar min-h-96"
                    />
                </div>

                <div
                    className="px-16 space-y-16 overflow-auto bg-white"
                    style={{ height: 'calc(100% - 150px)' }}
                >
                    {visibleList.map((item) => {
                        const isDefaultAdvices = ADVICES[item.id];
                        return (
                            <div className="template-list flex justify-between">
                                <div
                                    onClick={this.onCheckboxChange(
                                        item,
                                        advices.find((a) => a.text === item.text) === undefined,
                                    )}
                                    key={item.id}
                                    className="flex space-x-16 leading-6 cursor-pointer"
                                >
                                    <div>
                                        <div className="flex flex-col items-center justify-center w-18 h-18">
                                            <div className="relative flex flex-col items-center justify-center w-full h-full rounded-2">
                                                <input
                                                    type="checkbox"
                                                    className="w-full h-full opacity-0"
                                                    name="checkbox"
                                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                    // @ts-ignore
                                                    checked={
                                                        advices.find(
                                                            (a) => a.text === item.text,
                                                        ) !== undefined
                                                    }
                                                />
                                                <div className="absolute inset-0 checkbox-fill"></div>
                                                <svg
                                                    width="28"
                                                    height="28"
                                                    viewBox="0 0 28 28"
                                                    className="absolute hidden -top-3 -left-3 fill-white cursor-pointer absolute hidden -top-3 -left-3 fill-white"
                                                    preserveAspectRatio="xMidYMid meet"
                                                >
                                                    <path d="M10.0547 16.8125C10.2891 17.0469 10.6875 17.0469 10.9219 16.8125L17.8125 9.92188C18.0469 9.6875 18.0469 9.28906 17.8125 9.05469L16.9688 8.21094C16.7344 7.97656 16.3594 7.97656 16.125 8.21094L10.5 13.8359L7.85156 11.2109C7.61719 10.9766 7.24219 10.9766 7.00781 11.2109L6.16406 12.0547C5.92969 12.2891 5.92969 12.6875 6.16406 12.9219L10.0547 16.8125Z"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-neutral-3 text-14 pointer-events-none">
                                        <div
                                            className="flex flex-col tiny-mce truncate-overflow-2"
                                            dangerouslySetInnerHTML={{
                                                __html: item?.text || item?.parsedText,
                                            }}
                                        />
                                    </p>
                                </div>

                                {!isDefaultAdvices && (
                                    <div className="flex space-x-10">
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 13 13"
                                            fill="rgb(97, 106, 143)"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="delete-icon cursor-pointer"
                                            onClick={() => this.onEditAdvices(item)}
                                        >
                                            <path
                                                d="M12.1484 2.25781L10.7422 0.851562C10.3203 0.40625 9.59375 0.40625 9.14844 0.851562L7.83594 2.16406L10.8125 5.16406L12.1484 3.85156C12.5938 3.40625 12.5938 2.67969 12.1484 2.25781ZM1.22656 8.77344C1.15625 8.82031 1.13281 8.89062 1.10938 8.96094L0.5 12.0312C0.453125 12.2891 0.640625 12.5 0.851562 12.5C0.875 12.5 0.921875 12.5 0.945312 12.5L4.01562 11.8906C4.08594 11.8672 4.15625 11.8438 4.22656 11.7734L10.2969 5.70312L7.29688 2.70312L1.22656 8.77344ZM3.57031 10.8359L1.8125 11.1875L2.16406 9.42969L2.75 8.84375V10.25H4.15625L3.57031 10.8359Z"
                                                fill="#3E445C"
                                            ></path>
                                        </svg>
                                        <div
                                            className="delete-icon cursor-pointer"
                                            onClick={() => {
                                                this.onDeleteAdvice(item.text, item.id);
                                            }}
                                        >
                                            <svg
                                                viewBox="64 64 896 896"
                                                focusable="false"
                                                data-icon="delete"
                                                width="1.1em"
                                                height="1.1em"
                                                fill="rgb(97, 106, 143)"
                                                aria-hidden="true"
                                            >
                                                <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {editAdvice && (
                    <EditAdvicesModal
                        allAdvices={allAdvices}
                        editAdvice={editAdvice}
                        setAllAdvices={this.setAllAdvices}
                        onEditAdvicesCancel={this.onEditAdvicesCancel}
                    />
                )}
            </Fragment>
        );
    }
}

export default connect((state: GlobalState) => ({ advices: getAdvices(state) }))(Advices);
